import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, COLLECTIONS } from '../config/firebase';

const AuthContext = createContext(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [initializing, setInitializing] = useState(true);

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
            setUser(firebaseUser);

            if (!firebaseUser) {
                setUserData(null);
                setLoading(false);
                setInitializing(false);
                return;
            }

            // Subscribe to user document changes
            const userDocRef = doc(db, COLLECTIONS.USERS, firebaseUser.uid);
            const unsubscribeSnapshot = onSnapshot(userDocRef, async (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const data = { uid: docSnapshot.id, ...docSnapshot.data() };
                    
                    // Check if user is blocked or suspended
                    if (data.status === 'blocked' || data.status === 'suspended') {
                        // Sign out blocked user
                        await auth.signOut();
                        setUser(null);
                        setUserData(null);
                        setLoading(false);
                        setInitializing(false);
                        alert('Your account has been blocked. Please contact support.');
                        return;
                    }
                    
                    setUserData(data);
                } else {
                    setUserData(null);
                }
                setLoading(false);
                setInitializing(false);
            }, (error) => {
                console.error('Error listening to user document:', error);
                setLoading(false);
                setInitializing(false);
            });

            return () => unsubscribeSnapshot();
        });

        return () => unsubscribeAuth();
    }, []);

    const refreshUserData = async () => {
        if (!user) return;

        try {
            const { getDoc } = await import('firebase/firestore');
            const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
            if (userDoc.exists()) {
                setUserData({ uid: userDoc.id, ...userDoc.data() });
            }
        } catch (error) {
            console.error('Error refreshing user data:', error);
        }
    };

    const value = {
        user,
        userData,
        loading,
        initializing,
        refreshUserData,
        isAuthenticated: !!user && !!userData,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
